import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';

/**
 * SMART on FHIR EHR launch: the browser half of the monorepo's
 * [app contract](https://github.com/primuminc/primum/blob/main/openspec/changes/add-smart-ehr-launch/primum-app-contract.md).
 *
 * The API is the SMART client. The app creates a binding that only this browser tab holds,
 * sends the EHR launch on to the API with the binding's hash, and on return redeems the
 * single-use login token with the binding itself. A binding is never accepted from a URL.
 */

export const SMART_BINDING_KEY = 'primum.smart.binding';

export const EHR_SESSION_KEY = 'primum.ehr.session';

export const SMART_API_LAUNCH_PATH = '/v2/public/smart/launch';

export const SMART_REFUSAL_REASONS = [
  'unknown_ehr',
  'connection_disabled',
  'ehr_denied',
  'expired',
  'not_a_practitioner',
  'npi_missing',
  'npi_not_found',
  'name_mismatch',
  'npi_conflict',
  'npi_changed',
  'account_ineligible',
  'no_account',
  'registry_unavailable',
  'server_error',
] as const;

export type SmartRefusalReason = (typeof SMART_REFUSAL_REASONS)[number];

const SUPPORT_SUFFIX = 'Contact support.';

const SMART_ERROR_COPY: Record<SmartRefusalReason, string> = {
  unknown_ehr: `Primum isn’t set up for this EHR yet. ${SUPPORT_SUFFIX}`,
  connection_disabled: `Primum isn’t set up for this EHR yet. ${SUPPORT_SUFFIX}`,
  ehr_denied: 'The EHR didn’t grant access. Try again from the EHR.',
  expired: 'This launch expired. Open Primum from your EHR again.',
  not_a_practitioner: 'Your EHR profile doesn’t identify you as a clinician with an NPI.',
  npi_missing: 'Your EHR profile doesn’t identify you as a clinician with an NPI.',
  npi_not_found: `We couldn’t match your EHR profile to the NPI registry. ${SUPPORT_SUFFIX}`,
  name_mismatch: `We couldn’t match your EHR profile to the NPI registry. ${SUPPORT_SUFFIX}`,
  npi_conflict: `We couldn’t sign you in automatically. ${SUPPORT_SUFFIX}`,
  npi_changed: `We couldn’t sign you in automatically. ${SUPPORT_SUFFIX}`,
  account_ineligible: `We couldn’t sign you in automatically. ${SUPPORT_SUFFIX}`,
  no_account: 'Your organization links existing Primum accounts only. Sign up or contact support.',
  registry_unavailable: 'Something went wrong on our side. Try again in a minute.',
  server_error: 'Something went wrong on our side. Try again in a minute.',
};

/** Narrows an untrusted `reason` param, treating anything unrecognised as a server error. */
export function toSmartRefusalReason(reason: unknown): SmartRefusalReason {
  return SMART_REFUSAL_REASONS.find((known) => known === reason) ?? 'server_error';
}

export function getSmartErrorCopy(reason: SmartRefusalReason): string {
  return SMART_ERROR_COPY[reason];
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** 32 random bytes, base64url without padding. */
export function createBinding(): string {
  const bytes = new Uint8Array(32);

  crypto.getRandomValues(bytes);

  return toBase64Url(bytes);
}

/** Lower-case hex SHA-256 of the binding string. */
export async function hashBinding(binding: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(binding));

  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

export function buildApiLaunchUrl(params: {
  apiBaseUrl: string;
  iss: string;
  launch: string;
  bindingHash: string;
}): string {
  const url = new URL(SMART_API_LAUNCH_PATH, params.apiBaseUrl);

  url.searchParams.set('iss', params.iss);

  url.searchParams.set('launch', params.launch);

  url.searchParams.set('bh', params.bindingHash);

  return url.toString();
}

/** Reads `code` and `u` from a `#code=…&u=…` fragment. */
export function parseSmartFragment(hash: string): { code: string; sub: string } | null {
  const fragment = new URLSearchParams(hash.replace(/^#/, ''));

  const code = fragment.get('code');

  const sub = fragment.get('u');

  return code && sub ? { code, sub } : null;
}

export function storeBinding(binding: string): void {
  try {
    sessionStorage.setItem(SMART_BINDING_KEY, binding);
  } catch {
    // Storage blocked: the callback will report the launch as expired.
  }
}

/** Reads the binding and deletes it, so it can redeem one token at most. */
export function takeBinding(): string | null {
  try {
    const binding = sessionStorage.getItem(SMART_BINDING_KEY);

    sessionStorage.removeItem(SMART_BINDING_KEY);

    return binding;
  } catch {
    return null;
  }
}

export function isEHRSession(): boolean {
  try {
    return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(EHR_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearEHRSession(): void {
  try {
    sessionStorage.removeItem(EHR_SESSION_KEY);
  } catch {
    // Nothing stored, nothing to clear.
  }
}

export function markEHRSession(): void {
  try {
    sessionStorage.setItem(EHR_SESSION_KEY, '1');
  } catch {
    // Without the flag a reload falls back to the default token storage.
  }
}

/**
 * True only for a web page running inside a frame. Native is never framed, and must answer false
 * explicitly: React Native defines a window global without the frame properties, so the browser
 * comparison alone reads every native screen as framed.
 */
export function isEmbeddedFrame(): boolean {
  if (!isWeb) {
    return false;
  }

  try {
    return typeof window !== 'undefined' && window.top !== window.self;
  } catch {
    return true;
  }
}

/** Accounts created from an EHR carry a placeholder address that is never mailed. */
export function isPlaceholderEmail(email: string | null | undefined): boolean {
  return !!email && email.toLowerCase().endsWith('@ehr.invalid');
}
